import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import HeroDemo from "@/components/HeroDemo";
import { useAuth } from "@/contexts/AuthContext";
import { useRole } from "@/hooks/useRole";

const MinimalistHero = () => {
  const { isAuthenticated } = useAuth();
  const { roles } = useRole();

  const teacherLink = isAuthenticated && roles.includes('teacher') ? '/teacher/dashboard' : '/auth';
  const studentLink = isAuthenticated && roles.includes('student') ? '/student/dashboard' : '/auth';

  return (
    <section className="relative overflow-hidden bg-primary">
      {/* Giant background brand text */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
        <h2 className="text-[18vw] font-black text-foreground/[0.04] leading-none tracking-tighter whitespace-nowrap">
          EduLabs
        </h2>
      </div>

      <div className="container mx-auto px-6 relative z-10 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left — Copy */}
          <div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-5xl sm:text-6xl md:text-7xl font-black text-foreground leading-[0.95] tracking-tight"
            >
              Learn.
              <br />
              Practice.
              <br />
              Excel.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg md:text-xl text-foreground/70 mt-8 max-w-md leading-relaxed"
            >
              Create exams, attempt them in real-time, and track your results — all in one platform built for students and teachers.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <Button
                asChild
                size="lg"
                className="rounded-full px-10 py-7 text-base font-bold bg-foreground text-background hover:bg-foreground/90 shadow-xl"
              >
                <Link to={teacherLink}>
                  Join as a Teacher <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="rounded-full px-10 py-7 text-base font-bold border-foreground/20 text-foreground hover:bg-foreground/5"
              >
                <Link to={studentLink}>Join as a Student</Link>
              </Button>
            </motion.div>
          </div>

          {/* Right — Animated Demo */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <HeroDemo />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default MinimalistHero;
